/**
 * src/lib/mergeWithOptimistic.js — Pure merge function for optimistic state reconciliation.
 *
 * Extracted from useAppData.js (Phase 80) for testability.
 * Merges server-fetched records with locally-optimistic state,
 * preserving date overrides within TTL and detecting visual changes.
 */

export const OPTIMISTIC_TTL_MS = 10000;

/**
 * TICKET-1327: ANY field change counts. This used to compare only five task keys
 * (status, task_name, estimated_hours, priority, department), so for contacts, vessels,
 * deals and clients a saved field never counted as a change and `prev` was kept: the
 * CEO saved a phone for a Noona contact, the DB had it, the modal never showed it.
 * Arrays/objects compare by value so a fresh-but-equal row is not a change.
 */
function rowChanged(a, b) {
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) {
    const x = a[k];
    const y = b[k];
    if (x === y) continue;
    if (x && y && typeof x === 'object' && typeof y === 'object' && JSON.stringify(x) === JSON.stringify(y)) continue;
    return true;
  }
  return false;
}

/**
 * Merge server data with optimistic local state.
 *
 * @param {Array} prev — current local records
 * @param {Array} newData — fresh records from server
 * @param {Map} optimisticTimestamps — Map<id, timestamp> of recently mutated records
 * @param {number} now — current timestamp (Date.now())
 * @returns {{ merged: Array, anyChanged: boolean, expiredIds: Array<{id: string, age_ms: number}> }}
 */
export function mergeWithOptimistic(prev, newData, optimisticTimestamps, now) {
  const arr = Array.isArray(newData) ? newData : [];
  const newIds = new Set(arr.map(r => r.id));
  const prevIds = new Set(prev.map(r => r.id));
  const hasAdditions = arr.some(r => !prevIds.has(r.id));
  const hasDeletions = prev.some(r => !newIds.has(r.id));

  // Fast path: additions or deletions with no optimistic records → use server data
  if (hasAdditions || hasDeletions) {
    const hasOptimistic = prev.some(r => newIds.has(r.id) && optimisticTimestamps.has(r.id));
    if (!hasOptimistic) return { merged: arr, anyChanged: true, expiredIds: [] };
  }

  const prevMap = new Map(prev.map(r => [r.id, r]));
  let anyChanged = false;
  const expiredIds = [];

  const merged = arr.map(newRec => {
    const oldRec = prevMap.get(newRec.id);
    if (!oldRec) { anyChanged = true; return newRec; }

    const mutatedAt = optimisticTimestamps.get(newRec.id);
    const withinTTL = mutatedAt && (now - mutatedAt) < OPTIMISTIC_TTL_MS;
    let mergedRec = newRec;

    if (withinTTL) {
      const overrides = {};
      // TICKET-1038: was `oldRec.X && newRec.X && oldRec.X !== newRec.X` — that
      // guard required BOTH sides truthy, so a pending CLEAR (oldRec.X === null)
      // never qualified and a stale non-null server echo won the merge, visibly
      // un-doing the clear on the next poll. Compare with !== so either side
      // being null/cleared still counts as a pending override.
      if (oldRec.start_date !== newRec.start_date) {
        overrides.start_date = oldRec.start_date;
      }
      if (oldRec.due_date !== newRec.due_date) {
        overrides.due_date = oldRec.due_date;
      }
      if (Object.keys(overrides).length) mergedRec = { ...newRec, ...overrides };
    } else if (mutatedAt) {
      optimisticTimestamps.delete(newRec.id);
      expiredIds.push({ id: newRec.id, age_ms: now - mutatedAt });
    }

    if (rowChanged(oldRec, mergedRec)) anyChanged = true;
    return mergedRec;
  });

  return { merged, anyChanged, expiredIds };
}
